import {
  customElement,
  DeesElement,
  property,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';
import './dotglobal-change-badge.js';

import { languageColor } from './dotglobal-language-colors.js';
import type * as interfaces from '../../ts/ffb.interfaces.js';

type TInlinePart = string | TemplateResult;

@customElement('dotglobal-repo-home')
export class DotglobalRepoHome extends DeesElement {
  @property({ type: Object })
  public accessor repo: interfaces.IPublicRepo | null = null;

  @property({ type: Array })
  public accessor contents: interfaces.IPublicContentItem[] = [];

  @property({ type: String })
  public accessor path = '';

  @property({ type: String })
  public accessor readme = '';

  @property({ type: Array })
  public accessor releases: interfaces.IPublicRelease[] = [];

  @property({ type: Array })
  public accessor activity: interfaces.IPublicActivityEntry[] = [];

  @property({ type: Array })
  public accessor commits: interfaces.IPublicCommit[] = [];

  @property({ type: Object })
  public accessor languages: Record<string, number> = {};

  public static styles = [
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        color: var(--text-primary, #0a0a0a);
        font-family: var(--font-sans);
      }

      * {
        box-sizing: border-box;
      }

      a {
        color: inherit;
        text-decoration: none;
      }

      button {
        border: 0;
        background: none;
        color: inherit;
        font: inherit;
        cursor: pointer;
      }

      dees-icon {
        display: inline-flex;
        flex-shrink: 0;
      }

      .page {
        width: 100%;
        max-width: var(--maxw, 1180px);
        margin: 0 auto;
        padding: 0 24px;
      }

      .repo-layout {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 248px;
        gap: 24px;
        align-items: start;
      }

      .repo-layout > * {
        min-width: 0;
      }

      .stack {
        display: flex;
        flex-direction: column;
      }

      .row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
      }

      .grow {
        flex: 1;
      }

      .card,
      .filecard,
      .digest {
        overflow: hidden;
        border: 1px solid var(--border-default, #e4e4e7);
        border-radius: 8px;
        background: var(--bg-primary, #ffffff);
      }

      .filecard {
        overflow-x: auto;
      }

      .card-h {
        min-height: 44px;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 0 16px;
        border-bottom: 1px solid var(--border-default, #e4e4e7);
        background: var(--bg-tertiary, #f4f4f5);
        font-size: 13px;
        font-weight: 600;
      }

      .card-b {
        padding: 15px;
      }

      .btn {
        height: 30px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 0 11px;
        border: 1px solid var(--border-strong, #cccccc);
        border-radius: 6px;
        color: var(--text-primary, #0a0a0a);
        background: var(--bg-tertiary, #f4f4f5);
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
      }

      .btn:hover {
        background: var(--hover, rgba(0, 0, 0, 0.04));
      }

      .btn.ghost {
        border-color: transparent;
        background: transparent;
        color: var(--text-secondary, #333333);
      }

      .muted {
        color: var(--text-muted, #71717a);
      }

      .sha {
        color: var(--accent, #3b82f6);
        font-family: var(--font-mono);
        font-size: 12px;
      }

      .avatar {
        position: relative;
        width: 20px;
        height: 20px;
        display: inline-grid;
        place-items: center;
        flex-shrink: 0;
        border-radius: 50%;
        color: var(--accent-fg, #ffffff);
        background: var(--accent, #3b82f6);
        font-size: 9px;
        font-weight: 600;
      }

      .avatar img {
        position: absolute;
        inset: 0;
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .breadcrumbs {
        display: flex;
        align-items: center;
        gap: 5px;
        min-width: 0;
        font-family: var(--font-mono);
        font-size: 14px;
      }

      .breadcrumbs button {
        color: var(--accent, #3b82f6);
      }

      .breadcrumbs button:hover {
        text-decoration: underline;
      }

      .breadcrumbs .sl {
        color: var(--text-muted, #71717a);
      }

      .commit-strip {
        min-height: 48px;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 0 14px;
        border-bottom: 1px solid var(--border-default, #e4e4e7);
        background: var(--bg-primary, #ffffff);
        font-size: 13px;
      }

      table.ftable {
        width: 100%;
        border-collapse: collapse;
        font-size: 13.5px;
      }

      .ftable tr {
        border-bottom: 1px solid var(--border-subtle, #eeeeef);
      }

      .ftable tr:last-child {
        border-bottom: 0;
      }

      .ftable tbody tr:hover {
        background: var(--row-hover, rgba(59, 130, 246, 0.06));
      }

      .ftable td {
        padding: 8px 14px;
        vertical-align: middle;
      }

      .ftable td.name {
        width: 1%;
        white-space: nowrap;
      }

      .fname {
        display: inline-flex;
        align-items: center;
        gap: 9px;
        color: var(--text-primary, #0a0a0a);
        cursor: pointer;
      }

      .fname:hover {
        color: var(--accent, #3b82f6);
        text-decoration: underline;
      }

      .cmsg {
        display: block;
        max-width: 380px;
        overflow: hidden;
        color: var(--text-muted, #71717a);
        font-size: 12.5px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .age {
        color: var(--text-muted, #71717a);
        font-size: 12.5px;
        text-align: right;
        white-space: nowrap;
      }

      .sidecol {
        display: flex;
        flex-direction: column;
        gap: 20px;
        min-width: 0;
      }

      .divider {
        height: 1px;
        background: var(--border-default, #e4e4e7);
      }

      .repo-desc {
        margin: 0;
        color: var(--text-secondary, #333333);
        font-size: 13px;
        line-height: 1.55;
      }

      .meta-row {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--text-muted, #71717a);
        font-size: 12.5px;
      }

      .badge,
      .topic {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 2px 8px;
        border-radius: 4px;
        color: var(--text-secondary, #333333);
        background: var(--bg-tertiary, #f4f4f5);
        font-size: 10.5px;
        font-weight: 500;
        white-space: nowrap;
      }

      .topic {
        border: 1px solid var(--border-default, #e4e4e7);
      }

      .lang-dot {
        width: 11px;
        height: 11px;
        border-radius: 50%;
        background: var(--accent, #3b82f6);
        flex-shrink: 0;
      }

      .langbar {
        height: 8px;
        display: flex;
        overflow: hidden;
        border-radius: 4px;
        background: var(--bg-tertiary, #f4f4f5);
      }

      .langbar span {
        display: block;
      }

      .md {
        color: var(--text-primary, #0a0a0a);
        font-size: 14.5px;
        line-height: 1.7;
        overflow-wrap: anywhere;
      }

      .md > *:first-child {
        margin-top: 0;
      }

      .md h1 {
        margin: 28px 0 14px;
        padding-bottom: 10px;
        border-bottom: 1px solid var(--border-default, #e4e4e7);
        font-family: var(--font-display);
        font-size: 26px;
        font-weight: 700;
        letter-spacing: -0.02em;
      }

      .md h2 {
        margin: 26px 0 12px;
        padding-bottom: 8px;
        border-bottom: 1px solid var(--border-subtle, #eeeeef);
        font-family: var(--font-display);
        font-size: 20px;
        font-weight: 700;
        letter-spacing: -0.01em;
      }

      .md h3 {
        margin: 22px 0 10px;
        font-size: 16px;
        font-weight: 600;
      }

      .md p {
        margin: 0 0 14px;
      }

      .md a {
        color: var(--accent, #3b82f6);
      }

      .md a:hover {
        text-decoration: underline;
      }

      .md ul,
      .md ol {
        margin: 0 0 14px;
        padding-left: 24px;
      }

      .md li {
        margin: 4px 0;
      }

      .md code {
        padding: 0.15em 0.4em;
        border-radius: 4px;
        background: var(--code-bg, #f4f4f5);
        font-family: var(--font-mono);
        font-size: 12.5px;
      }

      .md pre {
        margin: 0 0 16px;
        padding: 14px 16px;
        overflow: auto;
        border: 1px solid var(--border-default, #e4e4e7);
        border-radius: 6px;
        background: var(--code-bg, #f4f4f5);
        font-family: var(--font-mono);
        font-size: 12.5px;
        line-height: 1.6;
        white-space: pre;
      }

      .md pre code {
        padding: 0;
        background: none;
      }

      .md blockquote {
        margin: 0 0 14px;
        padding: 2px 16px;
        border-left: 3px solid var(--border-strong, #cccccc);
        color: var(--text-secondary, #333333);
      }

      .md table {
        border-collapse: collapse;
        margin: 0 0 16px;
        font-size: 13.5px;
      }

      .md th,
      .md td {
        border: 1px solid var(--border-default, #e4e4e7);
        padding: 7px 13px;
        text-align: left;
      }

      .md th {
        background: var(--bg-tertiary, #f4f4f5);
        font-weight: 600;
      }

      .md hr {
        margin: 22px 0;
        border: 0;
        border-top: 1px solid var(--border-default, #e4e4e7);
      }

      .tok-keyword {
        color: #7c3aed;
        font-weight: 600;
      }

      .tok-string {
        color: #15803d;
      }

      .tok-comment {
        color: var(--text-muted, #71717a);
        font-style: italic;
      }

      .tok-number {
        color: #b45309;
      }

      .digest {
        position: relative;
      }

      .digest::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 3px;
        background: linear-gradient(180deg, #8b5cf6, var(--accent, #3b82f6));
      }

      .digest-h {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 14px 16px 12px;
      }

      .digest-title {
        font-family: var(--font-display);
        font-size: 15px;
        font-weight: 700;
        letter-spacing: -0.01em;
      }

      .digest-body {
        padding: 0 16px 14px;
      }

      .digest-prose {
        margin: 0;
        color: var(--text-primary, #0a0a0a);
        font-size: 13.5px;
        line-height: 1.65;
      }

      .digest-stats {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 14px;
      }

      .digest-stat {
        display: flex;
        align-items: baseline;
        gap: 6px;
        padding: 6px 11px;
        border: 1px solid var(--border-default, #e4e4e7);
        border-radius: 6px;
        background: var(--bg-tertiary, #f4f4f5);
        font-size: 12px;
      }

      .digest-stat .n {
        color: var(--text-primary, #0a0a0a);
        font-family: var(--font-mono);
        font-size: 14px;
        font-weight: 700;
      }

      .digest-highlights {
        display: flex;
        flex-direction: column;
        margin-top: 18px;
        overflow: hidden;
        border: 1px solid var(--border-default, #e4e4e7);
        border-radius: 8px;
      }

      .digest-hl {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 10px 16px;
        border-bottom: 1px solid var(--border-subtle, #eeeeef);
        font-size: 13px;
      }

      .digest-hl:last-child {
        border-bottom: 0;
      }

      .hl-repo {
        flex-shrink: 0;
        color: var(--accent, #3b82f6);
        font-family: var(--font-mono);
        font-size: 12px;
      }

      .hl-text {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        color: var(--text-secondary, #333333);
        font-size: 13.5px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .hl-age {
        flex-shrink: 0;
        color: var(--text-muted, #71717a);
        font-size: 12.5px;
      }

      .agent-glyph {
        width: 40px;
        height: 40px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        border-radius: 6px;
        color: #8b5cf6;
        background: color-mix(in srgb, #8b5cf6 18%, transparent);
      }

      .empty {
        padding: 48px 20px;
        text-align: center;
        color: var(--text-muted, #71717a);
      }

      @media (max-width: 1100px) {
        .repo-layout {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 820px) {
        .page {
          padding: 0 16px;
        }

        .hide-mobile {
          display: none;
        }
      }
    `,
  ];

  public render(): TemplateResult {
    if (!this.repo) {
      return html`<div class="page"><div class="empty">Repository not found.</div></div>`;
    }

    return html`
      <div class="page repo-layout">
        <div class="stack" style="gap: 16px">
          ${!this.path ? this.renderActivityDigest() : ''}
          ${this.renderFileBrowser()}
          ${this.readme && !this.path ? this.renderReadmeCard() : ''}
        </div>
        <aside class="sidecol">
          ${this.renderAbout()}
          ${this.renderReleases()}
          ${this.renderLanguages()}
        </aside>
      </div>
    `;
  }

  private renderActivityDigest(): TemplateResult {
    const activity = this.getUniqueSortedActivity(this.activity);
    const latest = activity[0];
    const versions = new Set(activity.map((entry) => entry.tagName || entry.version));
    return html`
      <div class="digest">
        <div class="digest-h">
          <div class="agent-glyph">${this.icon('Tag')}</div>
          <div class="grow" style="min-width: 0">
            <div class="row" style="gap: 8px">
              <span class="digest-title">Recent activity</span>
              <span class="badge" style="color: #8b5cf6; background: color-mix(in srgb, #8b5cf6 16%, transparent)">${this.icon('Tag')} Changelog</span>
            </div>
            <div class="muted" style="font-size: 11.5px; margin-top: 3px">Tagged changelog entries · ${latest ? this.timeAgo(latest.createdAt) : 'no timestamp'}</div>
          </div>
        </div>
        <div class="digest-body">
          ${activity.length
            ? html`
                <p class="digest-prose">
                  The latest tagged changelog entry is <strong>${latest!.title}</strong> in <code>${this.repo?.slug}</code>.
                  ${activity.length} changelog entr${activity.length === 1 ? 'y is' : 'ies are'} available across ${versions.size} version${versions.size === 1 ? '' : 's'}.
                </p>
                <div class="digest-stats">
                  <div class="digest-stat"><span class="n">${activity.length}</span><span class="muted">entries</span></div>
                  <div class="digest-stat"><span class="n">${versions.size}</span><span class="muted">versions</span></div>
                  <div class="digest-stat"><span class="n">${this.releases.length}</span><span class="muted">releases</span></div>
                </div>
                <div class="digest-highlights">
                  ${activity.slice(0, 4).map((entry) => this.renderDigestHighlight(entry))}
                </div>
              `
            : html`<p class="digest-prose muted">No tagged changelog activity is available for this repository.</p>`}
        </div>
      </div>
    `;
  }

  private renderDigestHighlight(entryArg: interfaces.IPublicActivityEntry): TemplateResult {
    return html`
      <div class="digest-hl">
        <dotglobal-change-badge .kind=${entryArg.kind}></dotglobal-change-badge>
        <span class="hl-repo">${this.repo?.name ?? this.getRepoShortName(entryArg.repoSlug)}</span>
        <span class="hl-text">${entryArg.title}</span>
        <span class="hl-age" title=${entryArg.tagName}>${entryArg.version || entryArg.tagName}</span>
      </div>
    `;
  }

  private renderFileBrowser(): TemplateResult {
    const latestCommit = this.commits[0];
    const rows = this.path ? [this.getParentRow(), ...this.contents] : this.contents;
    return html`
      <div class="stack" style="gap: 16px">
        <div class="row" style="justify-content: space-between; gap: 10px">
          <div class="row" style="gap: 10px; min-width: 0">
            <button class="btn">${this.icon('GitBranch')} ${this.repo!.defaultBranch}${this.icon('ChevronDown')}</button>
            ${this.path ? this.renderBreadcrumbs(this.path) : ''}
          </div>
        </div>
        <div class="filecard">
          ${latestCommit
            ? html`
                <div class="commit-strip">
                  ${this.renderAvatar(latestCommit.authorName, latestCommit.authorAvatarUrl)}
                  <span style="font-weight: 500">${latestCommit.authorName}</span>
                  <span class="muted" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap">${latestCommit.title}</span>
                  <span class="grow"></span>
                  <span class="sha">${latestCommit.shortSha}</span>
                  <span class="muted hide-mobile" style="font-size: 12.5px">${this.timeAgo(latestCommit.createdAt)}</span>
                  <span class="row hide-mobile" style="gap: 6px; color: var(--text-muted, #71717a); font-size: 12.5px; border-left: 1px solid var(--border-default, #e4e4e7); padding-left: 12px">
                    ${this.icon('History')} <b style="color: var(--text-primary, #0a0a0a)">${this.commits.length}+</b> commits
                  </span>
                </div>
              `
            : ''}
          ${rows.length
            ? html`
                <table class="ftable">
                  <tbody>${rows.map((item) => this.renderFileRow(item))}</tbody>
                </table>
              `
            : html`<div class="empty">No files found for this path.</div>`}
        </div>
      </div>
    `;
  }

  private renderFileRow(itemArg: interfaces.IPublicContentItem): TemplateResult {
    const commitTitle = this.getCommitTitleForItem(itemArg);
    return html`
      <tr>
        <td class="name">
          <button class="fname" @click=${() => this.openContentItem(itemArg)}>
            <span style=${itemArg.type === 'dir' ? 'color: var(--accent, #3b82f6)' : 'color: var(--text-muted, #71717a)'}>
              ${itemArg.type === 'dir' ? this.icon(itemArg.name === '..' ? 'ArrowUp' : 'Folder') : this.icon(this.getFileIcon(itemArg.name))}
            </span>
            <span>${itemArg.name}</span>
          </button>
        </td>
        <td><span class="cmsg" title=${commitTitle}>${commitTitle}</span></td>
        <td><span class="age">${this.timeAgo(itemArg.lastAuthorDate)}</span></td>
      </tr>
    `;
  }

  private renderReadmeCard(): TemplateResult {
    const readmePath = this.getReadmePath();
    return html`
      <div class="card">
        <div class="card-h">
          ${this.icon('BookOpen')} <span class="grow">README.md</span>
          <button class="btn ghost" @click=${() => this.emitNavigate(this.getFileHash(this.repo!.org, this.repo!.name, readmePath))}>${this.icon('Code')} View file</button>
        </div>
        <div class="md" style="padding: 20px 24px">${this.renderMarkdown(this.readme, 160)}</div>
      </div>
    `;
  }

  private getReadmePath(): string {
    return this.contents.find((item) => item.type === 'file' && /^readme\.md$/i.test(item.name))?.path || 'README.md';
  }

  private renderAbout(): TemplateResult {
    return html`
      <div class="stack" style="gap: 10px">
        <div class="row" style="gap: 8px; font-weight: 600; font-size: 13px">About</div>
        <p class="repo-desc">${this.repo!.description || 'No description provided.'}</p>
        ${this.repo!.topics.length ? html`<div class="row" style="gap: 8px">${this.repo!.topics.slice(0, 8).map((topic) => html`<span class="topic">${topic}</span>`)}</div>` : ''}
        <div class="stack" style="gap: 8px; margin-top: 4px">
          <div class="meta-row">${this.icon('Star')} <b style="color: var(--text-primary, #0a0a0a)">${this.repo!.starsCount}</b> stars</div>
          <div class="meta-row">${this.icon('Eye')} <b style="color: var(--text-primary, #0a0a0a)">${this.repo!.watchersCount}</b> watching</div>
          <div class="meta-row">${this.icon('Scale')} License in repository files</div>
          <div class="meta-row">${this.icon('Package')} <span class="sha">@${this.repo!.org}/${this.repo!.name}</span></div>
        </div>
      </div>
    `;
  }

  private renderReleases(): TemplateResult {
    const latestRelease = this.releases[0];
    return html`
      <div class="stack" style="gap: 10px">
        <div class="row" style="gap: 8px; font-weight: 600; font-size: 13px; justify-content: space-between"><span>Releases</span></div>
        ${latestRelease
          ? html`
              <div class="row" style="gap: 8px; font-size: 12.5px">
                <span style="color: var(--success, #22c55e)">${this.icon('Tag')}</span>
                <b>${latestRelease.tagName}</b><span class="badge">Latest</span>
              </div>
              <div class="muted" style="font-size: 12px">${this.timeAgo(latestRelease.publishedAt)} · ${this.releases.length} releases</div>
            `
          : html`<div class="muted" style="font-size: 12.5px">No releases yet.</div>`}
      </div>
    `;
  }

  private renderLanguages(): TemplateResult {
    const entries = Object.entries(this.languages).sort((a, b) => b[1] - a[1]).slice(0, 6);
    const total = entries.reduce((sum, [, value]) => sum + value, 0);
    if (!entries.length) {
      return html``;
    }
    return html`
      <div class="stack" style="gap: 10px">
        <div style="font-weight: 600; font-size: 13px">Languages</div>
        <div class="langbar">
          ${entries.map(([language, value]) => html`<span title=${language} style=${`flex:${value}; background:${languageColor(language)}`}></span>`)}
        </div>
        <div class="row" style="gap: 12px; font-size: 12px">
          ${entries.map(([language, value]) => html`
            <span class="row" style="gap: 6px; color: var(--text-muted, #71717a)">
              <span class="lang-dot" style=${`background:${languageColor(language)}`}></span>${language} ${total ? Math.round((value / total) * 100) : 0}%
            </span>
          `)}
        </div>
      </div>
    `;
  }

  private renderBreadcrumbs(pathArg: string): TemplateResult {
    const parts = pathArg.split('/').filter(Boolean);
    return html`
      <div class="breadcrumbs">
        <button @click=${() => this.emitNavigate(this.getRepoHash(this.repo!.org, this.repo!.name))}>${this.repo!.name}</button>
        ${parts.map((part, index) => {
          const subPath = parts.slice(0, index + 1).join('/');
          return html`<span class="sl">/</span><button @click=${() => this.emitNavigate(this.getRepoHash(this.repo!.org, this.repo!.name, subPath))}>${part}</button>`;
        })}
      </div>
    `;
  }

  private renderMarkdown(markdownArg: string, maxLinesArg = 160): TemplateResult[] {
    const lines = markdownArg.split('\n').slice(0, maxLinesArg);
    const blocks: TemplateResult[] = [];
    let index = 0;

    while (index < lines.length) {
      const line = lines[index];
      const trimmed = line.trim();
      if (!trimmed) {
        index++;
        continue;
      }

      if (trimmed.startsWith('```')) {
        const language = trimmed.slice(3).trim().split(/\s+/)[0] ?? '';
        const codeLines: string[] = [];
        index++;
        while (index < lines.length && !lines[index].trim().startsWith('```')) {
          codeLines.push(lines[index]);
          index++;
        }
        index++;
        blocks.push(html`<pre><code>${this.renderHighlightedBlock(codeLines, language)}</code></pre>`);
        continue;
      }

      if (trimmed.startsWith('|') && lines[index + 1]?.trim().startsWith('|')) {
        const header = trimmed.split('|').slice(1, -1).map((cell) => cell.trim());
        index += 2;
        const rows: string[][] = [];
        while (index < lines.length && lines[index].trim().startsWith('|')) {
          rows.push(lines[index].trim().split('|').slice(1, -1).map((cell) => cell.trim()));
          index++;
        }
        blocks.push(html`
          <table>
            <thead><tr>${header.map((cell) => html`<th>${this.renderInlineMarkdown(cell)}</th>`)}</tr></thead>
            <tbody>${rows.map((row) => html`<tr>${row.map((cell) => html`<td>${this.renderInlineMarkdown(cell)}</td>`)}</tr>`)}</tbody>
          </table>
        `);
        continue;
      }

      if (trimmed.startsWith('>')) {
        const quoteLines: string[] = [];
        while (index < lines.length && lines[index].trim().startsWith('>')) {
          quoteLines.push(lines[index].trim().replace(/^>\s?/, ''));
          index++;
        }
        blocks.push(html`<blockquote>${this.renderInlineMarkdown(quoteLines.join(' '))}</blockquote>`);
        continue;
      }

      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        const items: string[] = [];
        while (index < lines.length && /^[-*]\s/.test(lines[index].trim())) {
          items.push(lines[index].trim().slice(2));
          index++;
        }
        blocks.push(html`<ul>${items.map((item) => html`<li>${this.renderInlineMarkdown(item)}</li>`)}</ul>`);
        continue;
      }

      if (/^\d+\.\s/.test(trimmed)) {
        const items: string[] = [];
        while (index < lines.length && /^\d+\.\s/.test(lines[index].trim())) {
          items.push(lines[index].trim().replace(/^\d+\.\s/, ''));
          index++;
        }
        blocks.push(html`<ol>${items.map((item) => html`<li>${this.renderInlineMarkdown(item)}</li>`)}</ol>`);
        continue;
      }

      if (/^---+\s*$/.test(trimmed)) {
        blocks.push(html`<hr />`);
        index++;
        continue;
      }

      if (trimmed.startsWith('### ')) {
        blocks.push(html`<h3>${this.renderInlineMarkdown(trimmed.slice(4))}</h3>`);
        index++;
        continue;
      }
      if (trimmed.startsWith('## ')) {
        blocks.push(html`<h2>${this.renderInlineMarkdown(trimmed.slice(3))}</h2>`);
        index++;
        continue;
      }
      if (trimmed.startsWith('# ')) {
        blocks.push(html`<h1>${this.renderInlineMarkdown(trimmed.slice(2))}</h1>`);
        index++;
        continue;
      }

      const paragraphLines: string[] = [];
      while (index < lines.length && lines[index].trim() && !/^(#{1,3}\s|```|>|[-*]\s|\d+\.\s|\||---)/.test(lines[index].trim())) {
        paragraphLines.push(lines[index].trim());
        index++;
      }
      blocks.push(html`<p>${this.renderInlineMarkdown(paragraphLines.join(' '))}</p>`);
    }

    if (markdownArg.split('\n').length > maxLinesArg) {
      blocks.push(html`<p class="muted">Markdown preview limited to first ${maxLinesArg} lines.</p>`);
    }
    return blocks;
  }

  private renderInlineMarkdown(textArg: string): TInlinePart[] {
    const parts: TInlinePart[] = [];
    const regex = /\*\*(.+?)\*\*|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|`([^`]+)`/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(textArg))) {
      if (match.index > lastIndex) {
        parts.push(textArg.slice(lastIndex, match.index));
      }
      if (match[1]) {
        parts.push(html`<strong>${match[1]}</strong>`);
      } else if (match[2] && match[3]) {
        parts.push(html`<a href=${match[3]} target="_blank" rel="noopener">${match[2]}</a>`);
      } else if (match[4]) {
        parts.push(html`<code>${match[4]}</code>`);
      }
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < textArg.length) {
      parts.push(textArg.slice(lastIndex));
    }
    return parts;
  }

  private renderHighlightedBlock(linesArg: string[], languageArg = ''): TemplateResult[] {
    return linesArg.map((line, index) => html`${this.renderHighlightedCode(line, languageArg)}${index < linesArg.length - 1 ? '\n' : ''}`);
  }

  private renderHighlightedCode(lineArg: string, languageArg = ''): TInlinePart[] {
    const parts: TInlinePart[] = [];
    const isShell = /^(bash|sh|shell|zsh)$/i.test(languageArg);
    const tokenRegex = isShell
      ? /(#.*$|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`(?:\\.|[^`\\])*`|\b(?:case|do|done|elif|else|esac|fi|for|function|if|in|then|while)\b|\b\d+(?:\.\d+)?\b)/g
      : /(\/\/.*$|\/\*[\s\S]*?\*\/|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`(?:\\.|[^`\\])*`|\b(?:async|await|break|case|catch|class|const|continue|default|else|export|extends|false|finally|for|from|function|if|implements|import|interface|let|new|null|private|protected|public|return|switch|throw|true|try|type|undefined|var|while)\b|\b\d+(?:\.\d+)?\b)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = tokenRegex.exec(lineArg))) {
      const token = match[0];
      if (match.index > lastIndex) {
        parts.push(lineArg.slice(lastIndex, match.index));
      }
      parts.push(html`<span class=${this.getTokenClass(token)}>${token}</span>`);
      lastIndex = match.index + token.length;
    }

    if (lastIndex < lineArg.length) {
      parts.push(lineArg.slice(lastIndex));
    }
    return parts;
  }

  private getTokenClass(tokenArg: string): string {
    if (tokenArg.startsWith('//') || tokenArg.startsWith('/*') || tokenArg.startsWith('#')) {
      return 'tok-comment';
    }
    if (/^["'`]/.test(tokenArg)) {
      return 'tok-string';
    }
    if (/^\d/.test(tokenArg)) {
      return 'tok-number';
    }
    return 'tok-keyword';
  }

  private getParentRow(): interfaces.IPublicContentItem {
    const parentPath = this.path.split('/').filter(Boolean).slice(0, -1).join('/');
    return {
      name: '..',
      path: parentPath,
      type: 'dir',
      size: 0,
      htmlUrl: '',
      downloadUrl: '',
      lastCommitSha: '',
      lastAuthorDate: '',
      lastCommitMessage: '',
    };
  }

  private getCommitTitleForItem(itemArg: interfaces.IPublicContentItem): string {
    if (itemArg.name === '..') {
      return 'Parent directory';
    }
    const commit = this.commits.find((candidate) => itemArg.lastCommitSha && candidate.sha.startsWith(itemArg.lastCommitSha));
    return itemArg.lastCommitMessage || commit?.title || itemArg.lastCommitSha.slice(0, 8) || 'Latest change unavailable';
  }

  private openContentItem(itemArg: interfaces.IPublicContentItem): void {
    if (itemArg.name === '..') {
      this.emitNavigate(this.getRepoHash(this.repo!.org, this.repo!.name, itemArg.path));
      return;
    }
    if (itemArg.type === 'dir') {
      this.emitNavigate(this.getRepoHash(this.repo!.org, this.repo!.name, itemArg.path));
      return;
    }
    this.emitNavigate(this.getFileHash(this.repo!.org, this.repo!.name, itemArg.path));
  }

  private renderAvatar(labelArg: string, avatarUrlArg = ''): TemplateResult {
    return html`
      <span class="avatar">
        ${this.getInitials(labelArg)}
        ${avatarUrlArg
          ? html`<img src=${avatarUrlArg} alt="" loading="lazy" referrerpolicy="no-referrer" @error=${(eventArg: Event) => { (eventArg.currentTarget as HTMLImageElement).style.display = 'none'; }} />`
          : ''}
      </span>
    `;
  }

  private emitNavigate(hashArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-navigate', {
      detail: { hash: hashArg },
      bubbles: true,
      composed: true,
    }));
  }

  private icon(nameArg: string): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${16}></dees-icon>`;
  }

  private getFileIcon(fileNameArg: string): string {
    return /\.(ts|js|json|tsx|jsx|mjs)$/i.test(fileNameArg) ? 'FileCode' : 'File';
  }

  private getRepoHash(orgArg: string, repoArg: string, pathArg = ''): string {
    const base = `#/repo/${encodeURIComponent(orgArg)}/${encodeURIComponent(repoArg)}`;
    return pathArg ? `${base}/src/${this.encodePath(pathArg)}` : base;
  }

  private getFileHash(orgArg: string, repoArg: string, pathArg: string): string {
    return `#/file/${encodeURIComponent(orgArg)}/${encodeURIComponent(repoArg)}/${this.encodePath(pathArg)}`;
  }

  private encodePath(pathArg: string): string {
    return pathArg.split('/').filter(Boolean).map((part) => encodeURIComponent(part)).join('/');
  }


  private getUniqueSortedActivity(activityArg: interfaces.IPublicActivityEntry[]): interfaces.IPublicActivityEntry[] {
    const seen = new Set<string>();
    return [...activityArg]
      .sort((entryA, entryB) => new Date(entryB.createdAt).getTime() - new Date(entryA.createdAt).getTime())
      .filter((entry) => {
        const key = entry.id || `${entry.repoSlug}|${entry.tagName}|${entry.title}`;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
  }

  private getRepoShortName(repoSlugArg: string): string {
    return repoSlugArg.split('/').pop() || repoSlugArg;
  }

  private getInitials(labelArg: string): string {
    const parts = labelArg.split(/[.\s/-]+/).filter(Boolean);
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'FG';
  }

  private timeAgo(timestampArg: string): string {
    if (!timestampArg) {
      return '';
    }
    const diffMs = Date.now() - new Date(timestampArg).getTime();
    if (!Number.isFinite(diffMs)) {
      return 'unknown';
    }
    const minutes = Math.max(1, Math.round(diffMs / 60000));
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 48) {
      return `${hours}h ago`;
    }
    const days = Math.round(hours / 24);
    if (days < 60) {
      return `${days}d ago`;
    }
    const months = Math.round(days / 30);
    return `${months}mo ago`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-repo-home': DotglobalRepoHome;
  }
}
